import React from 'react';
import { ShoppingCart, Plus, Trash2, Percent, BarChart2, Home, Droplet, Heart, Car, Wifi, ShoppingBag, Music, BookOpen, Coffee, Gift, Globe, Calendar, CreditCard, Github } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Секция управления расходами бюджета
 */
const ExpenseSection = ({ 
  formData,
  expenses,
  updateExpenseItem,
  removeExpenseItem,
  addExpenseItem,
  expenseCategories,
  errors
}) => {
  // Функция получения иконки по ID категории
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'housing': return <Home className="category-icon" />;
      case 'utilities': return <Droplet className="category-icon" />;
      case 'groceries': return <ShoppingCart className="category-icon" />;
      case 'health': return <Heart className="category-icon" />;
      case 'transport': return <Car className="category-icon" />;
      case 'communication': return <Wifi className="category-icon" />;
      case 'clothing': return <ShoppingBag className="category-icon" />;
      case 'entertainment': return <Music className="category-icon" />;
      case 'education': return <BookOpen className="category-icon" />;
      case 'restaurants': return <Coffee className="category-icon" />;
      case 'gifts': return <Gift className="category-icon" />;
      case 'travel': return <Globe className="category-icon" />;
      case 'subscriptions': return <Calendar className="category-icon" />;
      case 'loans': return <CreditCard className="category-icon" />;
      case 'pets': return <Github className="category-icon" />;
      default: return <Plus className="category-icon" />;
    }
  };

  // Расчет общей суммы расходов
  const totalExpenses = expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);

  // Расчет суммы обязательных расходов
  const essentialExpenses = expenses
    .filter(expense => expense.essential)
    .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);

  // Расчет процентного соотношения
  const essentialPercentage = totalExpenses > 0 ? (essentialExpenses / totalExpenses * 100).toFixed(0) : 0;

  // Функция-обработчик изменения полей расхода
  const handleExpenseChange = (id, field, value) => {
    updateExpenseItem(id, field, value);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <ShoppingCart className="w-5 h-5 mr-2 text-red-500" />
          Расходы
        </h3>
        <div className="text-sm">
          <div className="flex items-center">
            <span className="text-gray-600 dark:text-gray-400 mr-2">Всего:</span>
            <span className="font-semibold text-red-600 dark:text-red-400">{totalExpenses.toLocaleString('ru-RU')} ₽</span>
          </div>
          <div className="flex items-center text-xs mt-1">
            <span className="text-gray-500 dark:text-gray-400 mr-2">Обязательные:</span>
            <span className="font-medium text-amber-600 dark:text-amber-400">
              {essentialExpenses.toLocaleString('ru-RU')} ₽ ({essentialPercentage}%)
            </span>
          </div>
        </div>
      </div>

      {/* Список расходов */}
      {expenses.length === 0 ? (
        <div className="text-center p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">У вас пока нет добавленных расходов</p>
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {expenses.map((expense, index) => (
            <div 
              key={expense.id}
              className={`p-3 rounded-lg flex flex-wrap md:flex-nowrap items-start gap-2 group transition-colors ${
                expense.essential 
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800' 
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800'
              }`}
            >
              {/* Иконка категории */}
              <div className="self-center flex-shrink-0 hidden sm:block">
                {getCategoryIcon(expense.category)}
              </div>
              
              {/* Название расхода */}
              <div className="flex-grow min-w-[180px]">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`expense-name-${expense.id}`}>
                  Название
                </label>
                <input
                  type="text"
                  id={`expense-name-${expense.id}`}
                  value={expense.name}
                  onChange={(e) => handleExpenseChange(expense.id, 'name', e.target.value)}
                  className={`form-input w-full ${errors[`expense_${index}_name`] ? 'input-error' : ''}`}
                />
                {errors[`expense_${index}_name`] && (
                  <p className="text-xs text-red-500">{errors[`expense_${index}_name`]}</p>
                )}
              </div>
              
              {/* Сумма расхода */}
              <div className="w-full sm:w-32">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`expense-amount-${expense.id}`}>
                  Сумма (₽)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id={`expense-amount-${expense.id}`}
                    value={expense.amount}
                    onChange={(e) => handleExpenseChange(expense.id, 'amount', e.target.value)}
                    className={`form-input w-full pr-6 ${errors[`expense_${index}_amount`] ? 'input-error' : ''}`}
                  />
                  <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">₽</span>
                </div>
                {errors[`expense_${index}_amount`] && (
                  <p className="text-xs text-red-500">{errors[`expense_${index}_amount`]}</p>
                )}
              </div>
              
              {/* Категория расхода */}
              <div className="w-full sm:w-40">
                <label className="text-xs text-gray-600 dark:text-gray-400" htmlFor={`expense-category-${expense.id}`}>
                  Категория
                </label>
                <select
                  id={`expense-category-${expense.id}`}
                  value={expense.category}
                  onChange={(e) => {
                    // Также обновляем обязательность в зависимости от категории
                    const category = expenseCategories.find(c => c.id === e.target.value);
                    handleExpenseChange(expense.id, 'category', e.target.value);
                    handleExpenseChange(expense.id, 'essential', category ? category.essential : false);
                  }}
                  className="form-select w-full"
                >
                  {expenseCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Чекбоксы */}
              <div className="flex-shrink-0 w-full sm:w-auto flex sm:flex-col sm:items-start gap-4 sm:gap-2 mt-2 sm:mt-5">
                {/* Обязательность расхода */}
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={expense.essential}
                    onChange={(e) => handleExpenseChange(expense.id, 'essential', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                    Обязательный
                  </span>
                </label>
                
                {/* Регулярность расхода */}
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={expense.recurring}
                    onChange={(e) => handleExpenseChange(expense.id, 'recurring', e.target.checked)}
                    className="form-checkbox"
                  />
                  <span className="ml-2 text-xs text-gray-600 dark:text-gray-400">
                    Регулярный
                  </span>
                </label>
              </div>
              
              {/* Кнопка удаления */}
              <div className="flex-shrink-0 mt-4 sm:mt-6">
                <button
                  type="button"
                  onClick={() => removeExpenseItem(expense.id)}
                  className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 rounded-full transition-colors"
                  aria-label="Удалить расход"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Кнопка добавления расхода */}
      <div className="mt-4">
        <Button
          type="button"
          onClick={addExpenseItem}
          icon={<Plus className="w-4 h-4" />}
          variant="outline"
          size="sm"
        >
          Добавить статью расходов
        </Button>
      </div>

      {/* Полезные советы */}
      <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
        <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2 flex items-center">
          <BarChart2 className="w-4 h-4 mr-1" />
          Полезно знать о расходах
        </h4>
        <ul className="text-xs text-red-600 dark:text-red-400 space-y-1 list-disc list-inside">
          <li>Для финансовой стабильности обязательные расходы не должны превышать 50-60% дохода</li>
          <li>Отмечайте как "обязательные" те расходы, без которых нельзя обойтись (жилье, еда, лекарства)</li>
          <li>Средние расходы на питание в России составляют около 30-35% семейного бюджета</li>
          <li>Расходы на жилье рекомендуется держать в пределах 30% от дохода</li>
          <li>Регулярно пересматривайте свои подписки и ежемесячные платежи – они часто становятся скрытой утечкой бюджета</li>
        </ul>
      </div>
    </div>
  );
};

export default ExpenseSection;